import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

const STATIC_PANELS = {
  Start: ["Hello {username}, ", "Salut {username}, "],
  Cours: [
    " Quel est le cours concerné ?",
    " Tu peux me donner un exemple de ce qu'il manque pour que je puisse retravailler ça ? ",
    " Je vais travailler à améliorer ça, merci de me le faire savoir ! ",
    " Je remarque que tes cours sont déjà très cours au départ, du coup c'est assez compliqué de résumer plus que ça.",
  ],
  Amelioration: [
    " Je ne suis pas sûr de comprendre, tu peux m'expliquer ce que tu veux dire ?",
    " Super idée, tu peux m'en dire plus sur comment tu imaginerais ça ?",
    " On va essayer d'améliorer ce point là.",
    " Pour l'instant l'app n'est dispo qu'en français, mais on va essayer de l'ouvrir à d'autres langues au plus vite ! ",
  ],
  Bug: [
    " Tu peux m'expliquer précisement ce qui ne fonctionne pas ?",
    " Merci de m'avoir signalé ce bug, je vais le corriger au plus vite ! ",
    " Merci bcp pour ton retour on va y travailler ! ",
  ],
  Assistance: [
    " Pour créer un cours tu peux aller sur le petit plus puis suivre les instructions pour ajouter ton document :)",
    " En version gratuite tu peux créer jusqu'à 4 cours par semaine, et en version premium tu peux créer autant de cours que tu veux !",
  ],
  End: [
    " Si tu as besoin de quoi que ce soit n’hésite pas ! User de Studizy",
    " N’hésite pas si tu as d’autres questions. À bientôt ! User de Studizy",
  ],
  // add more panels/categories here
};
const API_URL = process.env.REACT_APP_API_URL;
const fetchConfig = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

export default function MessageBuilderModal({
  isOpen,
  onClose,
  onInsert,
  username,
  context,
}) {
  const isMobile = window.innerWidth <= 768;
  const [activePanel, setActivePanel] = useState(Object.keys(STATIC_PANELS)[0]);
  const [builtBlocks, setBuiltBlocks] = useState([]);
  const [newText, setNewText] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [panels, setPanels] = useState(STATIC_PANELS);

  // derive adminName
  const rawAdmin = localStorage.getItem("adminName") || "Support";
  const adminName =
    rawAdmin.charAt(0).toUpperCase() + rawAdmin.slice(1).toLowerCase();

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      // fetch custom and merge
      try {
        const res = await fetch(
          `${API_URL}/support/custom/messages`,
          fetchConfig
        );
        const data = await res.json();
        const merged = { ...STATIC_PANELS };
        Object.entries(data.messages || {}).forEach(([cat, msgs]) => {
          merged[cat] = [...(STATIC_PANELS[cat] || []), ...msgs];
        });
        setPanels(merged);
        setActivePanel(Object.keys(merged)[0]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isOpen]);

  const addCustom = async () => {
    if (!newText.trim()) return;
    const trimmed = newText.trim();
    await fetch(`${API_URL}/support/custom/messages/${activePanel}/text`, {
      ...fetchConfig,
      method: "POST",
      body: JSON.stringify({ text: trimmed }),
    });
    setNewText("");
    setPanels((prev) => ({
      ...prev,
      [activePanel]: [...(prev[activePanel] || []), trimmed],
    }));
  };

  const reorderBlocks = (src, dest) => {
    setBuiltBlocks((arr) => {
      const a = [...arr];
      const [m] = a.splice(src, 1);
      a.splice(dest, 0, m);
      return a;
    });
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    const trimmedCat = newCategory.trim();
    try {
      await fetch(`${API_URL}/support/custom/messages/category`, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify({ name: trimmedCat }),
      });
      setPanels((prev) => ({
        ...STATIC_PANELS,
        ...prev,
        [trimmedCat]: [],
      }));
      setActivePanel(trimmedCat);
      setNewCategory("");
    } catch (e) {
      console.error("Failed to add category", e);
    }
  };

  if (!isOpen) return null;

  const addBlock = (text) => {
    let replaced = text
      .replace("{username}", username)
      .replace("{name}", username)
      .replace("{topic}", context);
    replaced = replaced.replace(/User/g, adminName);
    setBuiltBlocks((arr) => [...arr, replaced]);
  };
  const removeBlock = (idx) => {
    setBuiltBlocks((arr) => arr.filter((_, i) => i !== idx));
  };
  const finalMessage = builtBlocks.join("");

  return (
    <div
      className="modal is-active"
      style={{ overflowY: isMobile ? "auto" : "visible", zIndex: 5000 }}
    >
      <div className="modal-background" onClick={onClose} />
      <div
        className="modal-card"
        style={{
          width: "90%",
          maxWidth: "600px",
          maxHeight: isMobile ? "90vh" : "auto",
          margin: isMobile ? "1rem auto" : undefined,
        }}
      >
        <header className="modal-card-head">
          <p className="modal-card-title">Message Builder</p>
          <button className="delete" aria-label="close" onClick={onClose} />
        </header>
        <section
          className="modal-card-body"
          style={{
            overflowY: "auto",
            maxHeight: isMobile ? "60vh" : "auto",
          }}
        >
          {/* New Category Form */}
          <div className="field has-addons mb-4">
            <div className="control is-expanded">
              <input
                className="input"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="New category name"
              />
            </div>
            <div className="control">
              <button className="button is-primary" onClick={addCategory}>
                Add Category
              </button>
            </div>
          </div>
          {/* Category Tabs */}
          <div className="tabs">
            <ul>
              {Object.keys(panels).map((p) => (
                <li key={p} className={p === activePanel ? "is-active" : ""}>
                  <a onClick={() => setActivePanel(p)}>{p}</a>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="box"
            style={{
              minHeight: "100px",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {(panels[activePanel] || []).map((txt, i) => {
              // apply username and adminName replacements
              let label = txt
                .replace("{username}", username)
                .replace("{name}", username);
              label = label.replace(/User/g, adminName);
              return (
                <button
                  key={i}
                  className="button is-light is-fullwidth mb-2"
                  style={{
                    textAlign: "left",
                    whiteSpace: "normal",
                    textOverflow: "ellipsis",
                  }}
                  draggable
                  onClick={() => addBlock(txt)}
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", txt)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <h6 className="subtitle is-6">Preview (drag to reorder/remove):</h6>
          <div
            className="tags"
            style={{
              minHeight: "80px",
              border: "1px solid #ddd",
              padding: "0.5rem",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {builtBlocks.map((blk, idx) => (
              <span
                key={idx}
                className="tag is-link is-light mr-1 mb-1"
                draggable
                onDragStart={(e) => e.dataTransfer.setData("sourceIdx", idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const src = Number(e.dataTransfer.getData("sourceIdx"));
                  reorderBlocks(src, idx);
                }}
              >
                {blk}
                <button
                  type="button"
                  className="delete is-small ml-1"
                  onClick={() => removeBlock(idx)}
                />
              </span>
            ))}
          </div>
          {/* add sentence form */}
          <div className="field has-addons mt-3">
            <div className="control is-expanded">
              <input
                className="input"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="New sentence"
              />
            </div>
            <div className="control">
              <button className="button is-link" onClick={addCustom}>
                Add Sentence
              </button>
            </div>
          </div>
        </section>
        <footer className="modal-card-foot is-justify-content-space-between">
          <button className="button" onClick={() => setBuiltBlocks([])}>
            Clear All
          </button>
          <div>
            <button
              className="button is-primary"
              onClick={() => onInsert(finalMessage)}
            >
              Insert
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

MessageBuilderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  context: PropTypes.string,
};
